import { Component, OnInit } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { Brand } from "../../../features/brand/domain/models/brand";
import { BrandCampaign } from "../../../shared/models/brandCampaign";
import { CampaignType } from "../../../shared/enums/campaign.enums";
import { BrandService } from "../../../shared/services/api/brand.service";
import { NotificationService } from "../../../shared/services/notification.service";
import { lastValueFrom } from "rxjs";
import { TrackingService } from "../../../shared/analytics/tracking.service";
import { BrandCampaignService } from "../shared/services/brand-campaign.service";
import { BetaCampaignTrackingEvents } from "../shared/tracking/beta-campaign.tracking-events";

export type BrandCampaignCreationDialogData = undefined;
export type BrandCampaignCreationDialogResult = BrandCampaign;

@Component({
  templateUrl: "./brand-campaign-creation-dialog.component.html",
  styleUrls: ["./brand-campaign-creation-dialog.component.scss"],
})
export class BrandCampaignCreationDialogComponent implements OnInit {
  public isCreating = false;
  public campaign: BrandCampaign;
  public CampaignType = CampaignType;

  private brand?: Brand;

  private hasBetaCampaignTypeFeatureEnabled = false;
  private betaCampaignEvents: BetaCampaignTrackingEvents;

  constructor(
    private readonly dialogRef: MatDialogRef<
      BrandCampaignCreationDialogComponent,
      BrandCampaignCreationDialogResult
    >,
    private readonly brandService: BrandService,
    private readonly brandCampaignService: BrandCampaignService,
    private readonly notificationService: NotificationService,
    trackingService: TrackingService,
  ) {
    this.campaign = new BrandCampaign();
    this.campaign.type = CampaignType.Contest;
    this.betaCampaignEvents = new BetaCampaignTrackingEvents(
      this.brandService.currentBrand.id,
      trackingService,
    );
  }

  public async ngOnInit(): Promise<void> {
    try {
      this.brand = this.brandService.currentBrand;
      this.campaign.type = CampaignType.DownloadOnly;
      this.hasBetaCampaignTypeFeatureEnabled =
        this.brandService.hasBetaCampaignTypeFeatureEnabled();
    } catch (e) {
      this.notificationService.error("shared.errorLoadingCurrentBrand");
    }
  }

  public async createCampaign(): Promise<void> {
    this.isCreating = true;

    if (this.campaign.type === CampaignType.Beta) {
      this.betaCampaignEvents.betaCampaignCreated();
    }

    try {
      const campaign = await lastValueFrom(
        this.brandCampaignService.createCampaign(this.campaign),
      );

      this.dialogRef.close(campaign);
    } catch {
      this.notificationService.error("shared.changesCouldNotBeSaved");
      this.isCreating = false;
    }
  }

  public selectType(campaignType: CampaignType): void {
    if (!this.hasAccess(campaignType)) {
      return;
    }
    this.campaign.type = campaignType;
  }

  public hasAccess(campaignType: CampaignType): boolean {
    if (!this.brand) {
      return false;
    }

    const campaignIsDownloadOnly =
      campaignType === this.CampaignType.DownloadOnly;
    const campaignIsDownloadOnlyWithShortLink =
      campaignType === this.CampaignType.DownloadOnlyWithShortLink;
    const campaignIsBeta = campaignType === this.CampaignType.Beta;

    if (campaignIsBeta) {
      return (
        (this.brand.hasStandardAccess || this.brand.hasFullAccess) &&
        this.hasBetaCampaignTypeFeatureEnabled
      );
    }

    if (this.brand.hasFullAccess) {
      return true;
    }

    if (
      this.brand.hasStandardAccess &&
      (campaignIsDownloadOnly ||
        campaignIsDownloadOnlyWithShortLink ||
        campaignIsBeta)
    ) {
      return true;
    }

    if (this.brand.hasBasicAccess && campaignIsDownloadOnly) {
      return true;
    }

    return false;
  }
}
